import type { BanDich } from '../../types';
import type { viKho } from './kho.vi';

export const enKho: Pick<BanDich, keyof typeof viKho> = {
  troLyAiKhoView: {
    tieuDe: 'Data store',
    phuDe: 'Pick a source to connect and sync it, declare its business meaning and note its terminology.',
    moiCachLay: 'All access modes',
    banSao: 'Replica',
    trucTiep: 'Direct query',
    theoKhuon: 'Answers from query templates',
    khongDocTrangThai: "Couldn't read status",
    dangDongBo: 'Syncing…',
    chuaKhaiBang: 'No tables declared yet',
    coBangDung: '{{n}} tables · {{dung}} stopped syncing',
    demBang: '{{n}} tables · {{dong}} rows',
    demBangTrucTiep: '{{n}} tables declared',
  },
  troLyAiKho: {
    thanhHanhDong: 'Source actions',
    tabViec: 'Work on this source',
    trucTiep: 'Direct query',
    tatCaNguon: 'All sources',
    khongCoNguon: 'Source not found',
    khongCoNguonMoTa:
      'Source “{{ma}}” does not exist, is an API source (see Query templates), or this account is not allowed to see it.',
    tieuDe: 'Data store',
    phuDe:
      "For sources that can't answer filtered queries, the platform syncs data into a replica on a schedule and queries the replica. Each table has a data timestamp, and every answer based on that table carries it.",
    dongBoXongCoDung:
      'Sync finished, {{n}} tables stopped midway: {{ds}}. See the reason and how to fix it in the “Last run” column.',
    dongBoBiNgat: 'The sync cycle was interrupted at table {{bang}} (server restarted?). Click Sync to run it again.',
    dongBoXong: 'Synced {{n}} tables',
    dongBoBatDau: 'Started syncing {{n}} tables in the background — follow progress under Data store status',
    khongDongBoDuoc: "Couldn't sync — {{loi}}",
    timThayDeXuat: 'Found {{n}} suggestions',
    khongThayDeXuat: 'No suggestions found',
    doTimHetGio:
      'Detection timed out: {{n}} suggestions found, {{boQua}} checks not run. Turn off unused tables and run it again for full coverage.',
    khongDoDuoc: "Couldn't run detection — {{loi}}",
    rongTieuDe: 'No data source connected yet',
    rongMoTa: "Go to Connections to add a source — a GraphQL service or a connection string to the customer's database.",
    sangKetNoi: 'Go to Connections',
    khaiBangTuNguon: 'Pick tables from database',
    doMaQuanHe: 'Detect codes & relations',
    goiYTaiHuongDan: 'Download a Word guide on declaring tables and columns — works even before any source is connected.',
    goiYKhaiBangTuNguon:
      "Read the source database schema, then open the table picker. Only needs a connection, no sync yet.",
    goiYDoMaQuanHe:
      "Use when the source database doesn't declare foreign keys: the system compares real values in the store to infer which tables join, which columns hold only a few fixed values, and which tables are no longer used. Needs data in the store.",
    goiYDongBoNgay: 'Sync new data from the source into the replica and re-measure statistics for each table.',
    dongBoNgay: 'Sync now',
    taiHuongDan: 'Declaration guide',
    tabNguon: 'Connect & sync',
    tabKhai: 'Business declaration',
    tabTriThuc: 'Attached knowledge',
    nhanNguon: 'Source:',
    luocDoRong:
      "No tables could be read. Check that the account has USAGE on the schema — a missing grant returns the same empty result as an empty database.",
    khongDocDuocLuocDo: "Couldn't read schema: {{loi}}",
    doThongKe: 'Measure column statistics',
    goiYDoThongKe:
      "Direct-query sources have no sync cycle. Re-measure statistics (distinct values, ranges, empty ratio) on the customer database when data changes a lot — large tables are sampled.",
    daDoThongKe: 'Measured {{n}} tables.',
    dangDoThongKe: 'Measuring every column on the database — large sources can take a few minutes.',
    khongDoDuocThongKe: "Couldn't measure statistics — {{loi}}",
    tabKhuon: 'Query templates',
    dangTheoKhuon:
      'This source answers from query templates, so question-based declaration has no effect. Only connection, sync and terminology remain here.',
    moKhuonNghiepVu: 'Open Query templates',
  },
  troLyAiKhoTriThuc: {
    moTa:
      "The assistant searches these collections when a question needs <b>rules, definitions or guidance</b> — things that aren't in the data tables. With none attached, it answers from data only.",
    daGan: 'Knowledge collections attached',
    khongGanDuoc: "Couldn't attach — {{loi}}",
    treoTieuDe: '{{n}} attached collections can no longer be read',
    treoGhi: 'The collection was deleted in AI Knowledge, or this account cannot read it. Codes no longer valid:',
    treoLuuDeGo: '. Save to remove these codes.',
    rongTieuDe: 'No knowledge collections yet',
    rongMoTa: 'Create a collection in AI Knowledge first, then come back here to attach it.',
    soBoChon: '{{n}} collections selected',
    chuaLuu: ' · not saved',
    tieuDe: 'Knowledge collections for this source',
  },
  troLyAiKhoSync: {
    colCapNhatDen: 'Data up to',
    dongBoLaiBang: 'Re-sync this table',
    thaoTacBang: 'Actions for table {{bang}}',
    colBang: 'Table',
    colSoDongDo: 'Rows (last measurement)',
    colCotDaDo: 'Columns measured',
    uocLuong: 'estimate',
    chuaDo: 'Not measured',
    oDoLuc: 'Statistics measured at',
    oDoLucSub: 'click “Measure column statistics” to update',
    colSoDong: 'Rows',
    colBietXoa: 'Deleted-row detection',
    colLanChay: 'Last run',
    tangDan: 'incremental sync',
    chepDayDu: 'full sync every cycle',
    chuaDongBo: 'not synced',
    giaDinhChiThem: 'assumed insert-only',
    rongCoNguon: 'No tables yet. Click “Pick tables from database” to read the structure and choose tables.',
    rongKhongNguon: 'No tables declared. Use “Pick tables from database”, or create the first replica with scripts/.',
    chuKyVuaChay: 'Latest cycle',
    thongKeChuKy:
      '{{cheDo}} · fetched {{doc}} rows · wrote {{ghi}} rows to the store · deleted {{xoa}} rows · {{giay}} s',
    cheDoDayDu: 'Full sync',
    cheDoPhanMoi: 'Incremental sync',
    boDoThayGi: 'Found {{n}} suggestions',
    chacCao: 'fairly sure',
    chacVua: 'needs review',
    chacThap: 'just a guess',
    boDoGhi:
      'The system analyses the data already synced and proposes changes; you confirm each one. There is no approve-all button on purpose: a wrongly inferred relation goes straight into the query and produces a real number that answers the wrong question.',
    boDoRong:
      "Nothing found. If the store has no data yet, that's the correct result — the system needs real data to infer relations and value lists. Run Sync now, then detect again.",
    daGhi: 'recorded',
    ghiVaoBanKhai: 'Add to declaration',
    daGhiToast: 'Added to declaration — {{muc}}',
    khongGhiDuoc: "Couldn't record — {{loi}}",
    oBang: 'Tables declared',
    oDong: 'Rows synced',
    oTangDan: 'Incremental sync',
    oMoiNhat: 'Latest data',
    oTangDanSub: '{{n}} tables still fully synced',
    oBangSub: 'on this source',
    oDongSub: 'across all tables',
    chuaCo: '—',
    bangTieuDe: 'Data store status',
    tienDoDangChay: 'Syncing table {{bang}} ({{thuTu}}/{{soBang}}) · read {{daDoc}} rows · started {{batDau}}',
    tienDoChuanBi: 'Preparing to sync {{soBang}} tables (reading source schema) · started {{batDau}}',
    tienDoDoHoSo: 'Synced {{soBang}} tables · measuring data statistics',
    tienDoBiNgat:
      'The cycle started at {{batDau}} was interrupted at table {{bang}} ({{thuTu}}/{{soBang}}) — no heartbeat since {{capNhat}}. Click Sync to run it again.',
    trangThaiDangChay: 'running',
    trangThaiOk: 'succeeded',
    trangThaiDung: 'stopped midway',
    trangThaiBoQua: 'skipped',
    colDongBo: 'Sync',
    dongBoBangNhan: 'Re-sync table {{bang}} only',
    goiYDongBoBang: 'Re-run table {{bang}} only — other tables are not affected.',
    goiYDongBoBangDangBan:
      'This source has a sync cycle in progress. Wait for it to finish before syncing individual tables.',
    dongBoBangBatDau: 'Re-syncing table {{bang}} — follow it in the “Last run” column.',
    khongDongBoDuocBang: "Couldn't sync the table — {{loi}}",
    colXoa: 'Delete',
    xoaBangNhan: 'Delete table {{bang}} from the declaration',
    xacNhanXoaBang: 'Delete table “{{bang}}”?',
    xacNhanXoaBangGhi:
      "This table will be removed from the declaration and dropped from the replica, along with all synced data. This can't be undone — to use it again you must pick the table from the database and sync from scratch.",
    xoaBang: 'Delete table',
    huy: 'Cancel',
    daXoaBang: 'Deleted table {{bang}}.',
    xoaBangLamLech: '{{n}} business definitions lost their referenced table after the deletion: {{ds}}',
    xoaBangGoQuyen: 'This table was also removed from read access of {{n}} roles. Review it in Data permissions.',
    khongXoaDuocBang: "Couldn't delete the table — {{loi}}",
    oCheDo: 'Access mode',
    oCheDoTrucTiep: 'Direct',
    oCheDoTrucTiepSub: 'no replica — questions read the source directly',
  },
  troLyAiKhoSyncNhom: {
    quanHe: {
      ten: 'Relations between tables · {{n}}',
      mo: "Use when the source database doesn't declare foreign keys. The system compares real values in two columns to infer which tables join. Once added to the declaration, the assistant is allowed to JOIN those tables when answering.",
    },
    giaTri: {
      ten: 'Columns with only a few values · {{n}}',
      mo: 'Columns whose whole table uses just a few fixed values — status, type, region. Once declared, the assistant filters with the exact spelling. If they are abbreviated codes, go to the Business declaration tab to give each code its meaning.',
    },
    bangChet: {
      ten: 'Tables possibly no longer used · {{n}}',
      mo: 'No new rows for a long time. Adding it to the declaration turns the table off — extra tables raise the risk of the assistant picking the wrong numbers. Skip tables that are only updated seasonally.',
    },
    soLech: {
      ten: 'Row count mismatch · {{n}}',
      mo: 'The row count given to the assistant differs from the real count in the store. Nothing to record here — run Sync now once, then detect again.',
    },
  },
  troLyAiKhoKhai: {
    nghiaCuaMa: '{{cau}} — {{ma}}',
    chonTep: 'Choose answer file',
    hauQua: {
      sai_am_tham: 'wrong answer nobody notices',
      bi_tu_choi: 'refused, the user sees it',
      kho_doc: 'only harder to read',
      'sai_am_tham,bi_tu_choi': 'handle first',
    },
    khongSinhDuoc: "Couldn't generate questions — {{loi}}",
    daGhiCoBoQua: 'Saved {{n}} declarations · {{m}} questions skipped: {{ds}}',
    daGhiCoDeLen: '{{n}} terms with the same name overwrote older declarations — the old versions are gone: {{ds}}',
    daGhi: 'Saved {{n}} declarations',
    khongGhiDuoc: "Couldn't save — {{loi}}",
    khongRoBang: '(unknown table)',
    tieuDe: 'Business questions',
    moTa: 'Gaps in the declaration, ordered by <b>impact when missing</b>. Each option comes with figures from your own data to make the decision easier.',
    hoiLai: 'Ask again',
    batDau: 'Start',
    moiBatDau: 'Click <b>Start</b> to read the measured data statistics and list the gaps in the declaration.',
    daDu: 'The declaration is complete — nothing left to ask.',
    chipHauQua: '{{n}} questions · {{mo}}',
    luu: 'Save',
    luuSoLoiKhai: 'Save {{n}} declarations',
    duyetCaBang: "Approve this table's {{n}} suggestions",
    duyetTatCa: 'Approve all {{n}} suggestions (all tables)',
    duyetTatCaMoiBang: 'Approve all suggestions (all tables)',
    daDuyetTatCa: 'Approved {{n}} suggestions across all tables — review, then click Save.',
    khongConGoiY: 'No unapproved suggestions left.',
    goiYChuaDuyet: "Unapproved suggestions don't count as declarations.",
    traLoiPlaceholder: 'Answer…',
    nghiaPlaceholder: 'What does this code mean…',
    daDuyet: 'approved',
    duyet: 'Approve',
    goiYNhan: 'suggestion · not approved — not counted yet',
    goiYCanCu: 'Basis:',
    tomTatSaiAmTham: '{{n}} questions left that could silently produce wrong figures',
    tomTatBiTuChoi: '{{n}} questions will be blocked when asked',
    tomTatKhoDoc: '{{n}} questions only affect display',
    phaiGatTungCai:
      '{{n}} suggestions are left out of bulk approval: questions where a wrong answer still shows a number nobody notices, and suggestions guessed only from the column name. Both kinds must be confirmed one by one.',
    locTieuDe: 'Filter',
    locMo: 'Opens on “handle first” by default. Click another filter to see everything — then pick a table and the list on the right follows.',
    cauCanKhai: 'Questions to declare',
    chuaChonBang: 'Pick a table on the left to see its questions.',
    dangTaiBang: "Loading this table's questions…",
    locKhongCoCau: 'No questions at this impact level. Clear the filter to see them all.',
    phamViCoHet: 'Answer Yes for {{n}} unanswered tables',
    phamViKhongHet: 'Answer No for {{n}} tables',
    phamViLoatXong: 'Scope confirmed for {{n}} tables. Tables you answered yourself are unchanged.',
    phamViLoatHong: "Couldn't confirm: {{loi}}",
    moTaDangDoc: 'Reading answer file…',
    moTaDaChonSan: 'Pre-selected {{n}} multiple-choice answers — review, then click Save.',
    moTaHong: "Couldn't read the answer file: {{loi}}",
    tepRong: 'The file has no readable text.',
    xuatCauHoi: 'Export {{n}} questions',
    xuatHong: "Couldn't export: {{loi}}",
    moTaTaiLenBo: 'Upload answer file · {{n}} questions',
    tepSaiDinhDang: 'Only {{duoi}} answer files are accepted — the template downloaded from Export.',
    tuTepTraLoi: 'From answer file',
    tepDaDoc: 'Read {{n}} answers from the file. Review, then click Approve.',
    tepKhongCoTraLoi: 'The file has no answers matching open question codes.',
    tepCoLoi: '{{n}} lines/answers were dropped for wrong format or value — an error file was downloaded for fixing.',
  },
  troLyAiKhoKetQuaDo: {
    giay: '{{so}} s',
    tieuDe: 'Column statistics result',
    moTa: 'The platform just counted rows, distinct values, empty ratio, min/max and the value list of low-cardinality columns on every enabled table. The assistant uses these figures to understand what columns hold and to suggest declaration questions. Large tables are sampled.',
    bang: 'Tables measured',
    cot: 'Columns',
    dong: 'Rows read',
    thoiGian: 'Duration',
    boQuaTat: 'Skipped {{n}} disabled tables — turn them back on in the Declared business tab if they need measuring.',
    bangLoi: '{{n}} tables could not be measured',
    cotBo: "{{n}} columns could not be measured (usually long text such as ntext/image where the database can't count distinct values) — the table's other columns still have figures.",
    colBang: 'Table',
    colDong: 'Rows',
    colCot: 'Columns measured',
    colGhiChu: 'Notes',
    trenMau: 'sampled',
    cotKhongDo: 'skipped {{n}} columns: {{ds}}',
  },
  troLyAiKhoDaKhai: {
    phamViHoi: 'May the assistant take figures from this table',
    phamViCo: 'Yes — let the assistant use it',
    phamViKhong: 'No — leave this table out',
    phamViChua: 'Ask again in the interview',
    chiBangTat: 'Show only the {{n}} disabled tables',
    tab: 'Declared business',
    moTa: 'Current declaration. Edit in place — changes are recorded when you click Save.',
    luu: 'Save',
    choLuu: '{{n}} unsaved changes',
    daLuu: 'Saved {{n}} tables.',
    khongLuuDuoc: "Couldn't save: {{loi}}",
    rong: 'Nothing declared yet',
    rongMo: 'Answer a few questions in the Business declaration tab, then come back here to review and edit.',
    soCotDaKhai: '{{n}} columns declared',
    chuaKhai: 'not declared',
    grain: 'What does each row of this table represent',
    nhan: 'Business label',
    donVi: 'Unit',
    trongNghiaLa: 'What an empty cell means',
    soMa: '{{n}} codes explained',
    tat: 'disabled',
    congDonHoi: "Is it meaningful to add up this column's values?",
    congDonCo: 'Yes — gives a meaningful total',
    congDonKhong: 'No — ratio, unit price, threshold',
    congDonChua: 'Not declared',
    xoaKhai: 'Delete declaration',
    huy: 'Cancel',
    xacNhanXoaKhai: 'Delete the declaration of table “{{bang}}”?',
    xacNhanXoaKhaiGhi:
      "Only removes this table's business declaration: scope, table type, row meaning, labels, units, additivity and empty-cell meaning of every column. The table stays in the list picked from the database, keeps syncing, and its data in the store is untouched. It returns to Business declaration as if never declared.",
    daXoaKhai: 'Removed the declaration of “{{bang}}”.',
    daXoaKhaiNhieu: 'Removed the declarations of {{n}} tables.',
    xacNhanXoaKhaiNhieu: 'Delete the declarations of {{n}} selected tables?',
    xoaDaChon: 'Delete declarations ({{n}})',
    chonHet: 'Select all',
    khongXoaDuocKhai: "Couldn't delete: {{loi}}",
  },
  troLyAiKhoKhaiBang: {
    thieuBangDong:
      "{{n}} documents have their header table selected but not the line table: {{ds}}. Without line tables you can't ask at item level.",
    tichKemBangDong: 'Also select {{n}} line tables',
    soDong: '{{n}} rows',
    bangRong: 'empty',
    bangCuaKhach: 'your table',
    anBangRong: 'Hide {{n}} empty tables (no rows)',
    copyXong: 'Copied the {{m}}-column set to {{n}} tables. Those tables are now selected for declaration — review before saving.',
    tieuDe: 'Pick tables from database',
    moTa: "This is a proposed declaration, not saved yet. Untick tables that don't need syncing; the rest will be created in the replica database. Note: unticking an already declared table doesn't remove it — to remove it, delete the table in the “Connect & sync” tab.",
    canhBao: 'Skipped multi-column foreign keys: {{ds}}. Those relations must be declared manually.',
    daChon: 'Selected {{n}}/{{m}} tables',
    chonHet: 'Select all',
    boHet: 'Clear all',
    soCot: '{{n}} columns',
    tangDan: 'incremental sync',
    chepDay: 'full sync every time',
    khoaChinh: 'primary key: {{ds}}',
    khaiN: 'Save {{n}} tables',
    dangLuu: 'Saving {{n}}/{{m}} tables…',
    huy: 'Cancel',
    daKhai: 'Saved {{n}} tables, created {{m}} new tables in the data store.',
    khongKhaiDuoc: "Couldn't save: {{loi}}",
    timPlaceholder: 'Search tables by code or name — e.g. OINV, invoice',
    timKhongThay: 'No tables match.',
    dangLoc: '{{n}} after filter',
    chonHetLoc: 'Select {{n}} results',
    boHetLoc: 'Clear {{n}} results',
    hienThem: 'Show more ({{n}} more tables)',
    boQuaTong: 'Skipped {{n}} tables that could not be turned into a declaration:',
    thuGon: 'collapse',
    xemTen: 'Show names',
    lyDo: {
      qua_nhieu_cot: '{{n}} tables with more than 1000 columns',
      khoa_chinh_qua_dai: '{{n}} tables with a primary key over 12 columns',
      khoa_chinh_nullable: '{{n}} tables/views with a nullable primary key column',
      khac: '{{n}} tables for other reasons',
    },
    nhieuBang:
      "{{n}} tables selected. You can still save, but each one becomes a table in the replica, re-synced every cycle, and another line in the schema sent to the model. Prefer tables that actually hold the figures you plan to ask about.",
    soCotDaBo: 'keeping {{n}}/{{m}} columns',
    moChonCot: 'Choose columns for this table',
    cotMoi: '+{{n}} new columns at source',
    boCotRong: 'Drop {{n}} empty columns (all tables)',
    boCotRongMo: 'columns measured with no value at all — each costs ~50 tokens on every question',
    daBoCotRong: 'Dropped {{n}} empty columns. Open a table to re-select if needed, then click Save.',
    gomCanhBao: '{{n}} warning types — the most common appears on {{m}} tables',
    xemChiTiet: 'view',
    nBang: '{{n}} tables',
    moCanhBaoBang: "View this table's warnings",
    locNhanh: {
      co_dulieu: '{{n}} with data',
      da_khai: '{{n}} selected',
      rong_cot: '{{n}} tables ≥80 columns',
    },
  },
  troLyAiKhoCopyCot: {
    tieuDe: 'Copy the column set of “{{bang}}” to other tables',
    moTa: "Target tables keep exactly the {{n}} columns kept here, matched by column name (case-insensitive). The target's primary key, timestamp and relation columns are always kept. Add each table's own columns afterwards.",
    timPlaceholder: 'Search tables…',
    demUngVien: '{{n}}/{{m}} tables match at least one column',
    soKhop: '{{n}}/{{m}} columns',
    chonHet: 'Select all',
    boHet: 'Clear all',
    khongCoBang: 'No tables match the search.',
    huy: 'Cancel',
    apDung: 'Copy to {{n}} tables',
  },
  troLyAiKhoChonCot: {
    copySang: 'Copy this column set to other tables…',
    tim: 'Search columns…',
    giuHet: 'Keep all',
    boHet: 'Drop all',
    daGiu: "Keeping {{n}}/{{m}} columns. Dropped columns aren't created in the replica and aren't sent to the model as context.",
    khongThay: 'No columns match.',
    khoa: {
      kc: 'Primary key column — dropping it makes the declaration invalid.',
      moc: 'Timestamp column — dropping it forces a full sync every cycle.',
      lien: "Relation column — dropping it means the model won't know which table to JOIN.",
    },
  },
  troLyAiKhoThuatNgu: {
    tab: 'Notes & terminology',
    luu: 'Save',
    them: 'Add row',
    xoa: 'Delete row',
    luuHong: "Couldn't save — {{loi}}",
    tnTieuDe: 'Glossary',
    tnMoTa: "Explain your organization's own terms in words. The model reads them before writing a query.",
    tnThuatNgu: 'Term, e.g. total capital',
    tnNghia: 'Meaning — say which table and column it comes from, and what would be wrong',
    tnRong: 'No terms yet. The model currently guesses the meaning of domain terms on every question.',
    daLuuTn: 'Saved {{n}} terms',
    dnTieuDe: 'Business definitions',
    dnMoTa: 'Stricter than a term: ties a term directly to the SQL expression that computes it. The model uses the declared expression instead of inventing its own calculation.',
    dnThuatNgu: 'Term',
    dnBieuThuc: 'SQL expression, e.g. SUM(jdt1.credit - jdt1.debit)',
    dnPhuThuoc: 'Tables/columns the expression depends on, comma-separated',
    dnPhuThuocGhi:
      'Use `table` or `table.column`. When filled in, the platform automatically clears the verified status when the schema changes — leave it empty and you get no such warning.',
    dnRong: 'Use when a term is always computed with the same expression.',
    chuaXacNhan: 'Not verified',
    daLuuDn: 'Saved {{n}} definitions',
    tnRongTieuDe: 'No terms yet',
    dnRongTieuDe: 'No definitions yet',
    gcTieuDe: 'Business notes for the whole source',
    gcMoTa:
      'Guidance in words, added to the context of every question on this source. This is the only place to describe things spanning several tables — which table is a report, which is the ledger, which calculation was tried and is wrong.',
    gcViDu:
      'Example:\n- Balance sheet questions: use table foxai_bcdkt (form B01-DN), column ending is the closing balance, compare codes with LTRIM(RTRIM(code)). Code 440 is total capital, 300 liabilities, 400 owner equity, 280 total assets.\n- Do not rebuild the balance sheet from jdt1: summing by account class comes out ~73 billion short because it nets debit/credit balances within the same receivable account.\n- This whole source is one entity. profit_code and ocr_code4 are only filled on expense accounts.',
    gcDem: '{{n}}/{{tran}} characters — everything here is sent with every question',
    daLuuGhiChu: 'Business notes saved',
    chenMau: 'Insert VN accounting template ({{canCu}})',
    daChenMau: "Template inserted — read it and adapt it to your company's chart of accounts before saving.",
  },
};
